// Map filters, kept in the URL (?f=garden,dog,ale,food&chain=indie) so a
// shared link shows the same pubs. They apply to the map, score pins and
// the leaderboard alike.

import { Flag } from '../data/schema';

export type ChainFilter = 'all' | 'independent' | 'chain';

export interface Filters {
  /** Beer garden or outdoor seating confirmed (by tag or mapped next to the pub). */
  confirmed: boolean;
  dog: boolean;
  ale: boolean;
  food: boolean;
  chain: ChainFilter;
}

export const NO_FILTERS: Filters = {
  confirmed: false,
  dog: false,
  ale: false,
  food: false,
  chain: 'all',
};

const TOGGLES = [
  ['confirmed', 'garden'],
  ['dog', 'dog'],
  ['ale', 'ale'],
  ['food', 'food'],
] as const;

const CONFIRMED = Flag.Garden | Flag.Outdoor | Flag.GardenNearby | Flag.OutdoorNearby;

/** Does a pub (by its flag bits) pass the filters? */
export function passes(flags: number, f: Filters): boolean {
  if (f.confirmed && (flags & CONFIRMED) === 0) return false;
  if (f.dog && (flags & Flag.Dog) === 0) return false;
  if (f.ale && (flags & Flag.RealAle) === 0) return false;
  if (f.food && (flags & Flag.Food) === 0) return false;
  if (f.chain === 'independent' && (flags & Flag.Chain) !== 0) return false;
  if (f.chain === 'chain' && (flags & Flag.Chain) === 0) return false;
  return true;
}

export const isFiltered = (f: Filters) =>
  f.confirmed || f.dog || f.ale || f.food || f.chain !== 'all';

export function activeCount(f: Filters): number {
  return TOGGLES.filter(([key]) => f[key]).length + (f.chain === 'all' ? 0 : 1);
}

export function readFilters(search: string): Filters {
  const params = new URLSearchParams(search);
  const on = new Set((params.get('f') ?? '').split(','));
  const chain = params.get('chain');
  const filters: Filters = {
    ...NO_FILTERS,
    chain: chain === 'indie' ? 'independent' : chain === 'chain' ? 'chain' : 'all',
  };
  for (const [key, token] of TOGGLES) filters[key] = on.has(token);
  return filters;
}

/** Write the filters into a URL's search params (removing them when off). */
export function writeFilters(url: URL, f: Filters): URL {
  const tokens = TOGGLES.filter(([key]) => f[key]).map(([, token]) => token);
  if (tokens.length) url.searchParams.set('f', tokens.join(','));
  else url.searchParams.delete('f');
  if (f.chain === 'all') url.searchParams.delete('chain');
  else url.searchParams.set('chain', f.chain === 'independent' ? 'indie' : 'chain');
  return url;
}
